import { useMemo } from 'react';
import { Layer, Source } from 'react-map-gl';
import {
  CLUSTER_MIN_RADIUS,
  DOT_COLOUR,
  DOT_OUTLINE_COLOUR,
  DOT_OUTLINE_WIDTH,
  FOREST_PIN_MIN_ZOOM,
  GROUP_RING,
  PIN_RING,
  PIN_RING_KINDS,
  RINGS,
  clusterColourExpression,
  clusterDiscRadiusExpression,
  clusterOutlineWidthExpression,
  haloZoomFade,
  ringRadiusExpression,
  ringWidthExpression,
  type MarkerKind,
} from './markerPulse';

// Pin ids are negative so they can never collide with a supercluster cluster_id
// (always >= 0) when both share one Mapbox feature-state id-space (markerPulse.ts).
const withPulseId = (features: any[] | undefined): any[] =>
  (features ?? []).map((feature, index) => ({
    ...feature,
    properties: { ...feature.properties, pulse_id: -(index + 1) },
  }));

export const FarmetsGroupLayer = ({ propsData }: { propsData: any }) => {
  return (
    <Source
      id="farmers-group"
      type="geojson"
      // Founder 2026-09-21 (round 2): pulse_id per feature + promoteId so
      // useGroupRipple can set feature-state on the hovered / selected marker.
      data={{ type: 'FeatureCollection', features: withPulseId(propsData) }}
      promoteId="pulse_id"
    >
      {/* Ring-less: the ripple ring, invisible at rest (opacity 0). */}
      <Layer
        id="farmers-group-ring"
        type="circle"
        paint={{
          'circle-opacity': 0,
          'circle-radius': GROUP_RING.radius,
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': GROUP_RING.width,
          'circle-stroke-opacity': 0,
          'circle-translate': [GROUP_RING.translateX, GROUP_RING.translateY],
        }}
      />
      <Layer
        id="farmers-group"
        type="symbol"
        layout={{
          'icon-image': 'farmers-group',
          'icon-size': 1.2,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          // Founder 2026-09-21 (production review): no numbers on any marker.
          // SUPERSEDED in place (S2): the count badge that was drawn here -
          //   'text-allow-overlap': true,
          //   'text-font': ['Oswald Regular', 'Arial Unicode MS Bold'],
          //   'text-field': '{producers_count}',
          //   'text-size': 14,
          //   'text-anchor': 'top-right',
          //   'text-offset': ['case', ['<', ['get', 'producers_count'], 10],
          //     ['literal', [1.7, -3]], ['<', ['get', 'producers_count'], 100],
          //     ['literal', [2.0, -3]], ['literal', [2.1, -3]]],
          //   paint 'text-color': '#000'
          // Its removal also closes the Known issue of the literal "null"
          // printed on the two cohorts whose producers_count is null upstream.
        }}
        minzoom={0}
        maxzoom={22}
      />
    </Source>
  );
};

export const FactoriesGroupLayer = ({ propsData }: { propsData: any }) => {
  return (
    <Source id="factories-group" type="geojson" data={propsData}>
      <Layer
        id="factories-group"
        type="symbol"
        layout={{
          'icon-image': 'factories-group',
          'icon-size': 1.2,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'text-allow-overlap': true,
        }}
        paint={{
          'text-color': '#000',
        }}
        minzoom={0}
        maxzoom={22}
      />
    </Source>
  );
};

export const ForestsGroupLayer = ({ propsData }: { propsData: any }) => {
  return (
    <Source
      id="forests-group"
      type="geojson"
      // Founder 2026-09-21 (round 2): pulse_id + promoteId for the hover ripple.
      // propsData is a FeatureCollection here (unlike FarmersGroup's array).
      data={{ type: 'FeatureCollection', features: withPulseId(propsData?.features) }}
      promoteId="pulse_id"
    >
      {/* Ring-less: forests-group uses the tree pin icon, so its ring is
          PIN_RING; invisible at rest, ripples on hover / selection. */}
      <Layer
        id="forests-group-ring"
        type="circle"
        paint={{
          'circle-opacity': 0,
          'circle-radius': PIN_RING.radius,
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': PIN_RING.width,
          'circle-stroke-opacity': 0,
          'circle-translate': [0, PIN_RING.translateY],
        }}
      />
      <Layer
        id="forests-group"
        type="symbol"
        layout={{
          'icon-image': 'forests-group',
          'icon-size': 1.2,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'text-allow-overlap': true,
        }}
        paint={{
          'text-color': '#000',
        }}
        minzoom={0}
        maxzoom={22}
      />
    </Source>
  );
};

// D16a - polygon outline rendering, shared by the Mizan planting-zone layers
// and the computed tree_project_extents. Yellow (the brand accent already used
// for News fallbacks) outline with a light fill - deliberately NOT red and NOT
// a land-cover comparison (that is D16b, blocked on the green-claims review).
// One instance per source layer; `id` must be unique per instance.
export const OutlineLayer = ({ id, propsData }: { id: string; propsData: any }) => {
  return (
    <Source id={id} type="geojson" data={propsData}>
      <Layer
        id={`${id}-fill`}
        type="fill"
        paint={{
          'fill-color': '#ffd900',
          'fill-opacity': 0.12,
        }}
      />
      <Layer
        id={`${id}-line`}
        type="line"
        paint={{
          'line-color': '#ffd900',
          'line-width': 2,
        }}
      />
    </Source>
  );
};

// Map UX quest (2026-09-17/18, founder brief + two corrections). Farmers and
// trees share one layer stack, bottom to top:
//   <kind>-ring-2..0  the cluster's three concentric white rings (outer first).
//   <kind>-clusters   the core disc in the marker colour, radius stepped on
//                     point_count (CLUSTER_RADIUS_STEPS).
//   <kind>-pin-ring   the individual pin's one white ring, translated onto the
//                     icon's badge centre (see markerPulse.ts PIN_RING comment).
//   <kind>-marker     the pin icon, unchanged.
// SUPERSEDED in place (§2): the clusters used to be ONE symbol layer - the icon
// '<kind>-clusters' (farmers.png / trees.png: core disc + three white rings at
// opacity 1.0 / 0.6 / 0.3) plus a '{point_count_abbreviated}' text label (Oswald
// 26px, white). The count text is removed by founder decision (it now shows in a
// desktop hover tooltip, useClusterTooltip). The icon is rebuilt here as four
// circle layers with the PNG's own proportions, because a PNG cannot be resized
// per cluster or have its rings animated one by one. useMarkerPulse ripples the
// rings' opacity on the ONE hovered or selected marker (founder 2026-09-20;
// before that, one random marker at a time); everything else shows the static
// opacities declared below. The PNGs are still loaded by useMapImages and drawn as before
// (icon unchanged); the pin's own single ring is duplicated here as a circle
// layer so it, too, can be animated - the PNG's ring keeps rendering underneath
// it either way, so a non-rippling pin looks identical to before this quest.
const ClusteredPinLayer = ({ kind, propsData }: { kind: MarkerKind; propsData: any }) => {
  // Memoised as a whole: react-map-gl re-uploads a source whenever `data` is a
  // new object, which re-clusters everything (new cluster_ids) on every render.
  const data = useMemo(
    () =>
      ({
        type: 'FeatureCollection',
        features: withPulseId(propsData),
      }) as unknown as GeoJSON.FeatureCollection,
    [propsData],
  );
  return (
    <Source
      id={kind}
      type="geojson"
      data={data}
      cluster={true}
      clusterMaxZoom={15}
      clusterRadius={30}
      promoteId="pulse_id"
    >
      {[2, 1, 0].map((k) => (
        <Layer
          key={k}
          id={`${kind}-ring-${k}`}
          type="circle"
          filter={['has', 'point_count']}
          paint={{
            'circle-opacity': 0,
            'circle-radius': ringRadiusExpression(k),
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': ringWidthExpression(k),
            'circle-stroke-opacity': haloZoomFade(RINGS[k].staticOpacity),
          }}
        />
      ))}
      <Layer
        id={`${kind}-clusters`}
        type="circle"
        filter={['has', 'point_count']}
        paint={{
          // Founder 2026-09-21: the disc keeps the marker colour at full size
          // and turns brand yellow with a 1px dark outline as it shrinks, so
          // the small dots read against the forest.
          'circle-color': clusterColourExpression(kind),
          'circle-stroke-color': DOT_OUTLINE_COLOUR,
          'circle-stroke-width': clusterOutlineWidthExpression(),
          // Founder 2026-09-20: shrinks with zoom to a plain dot by the split.
          'circle-radius': clusterDiscRadiusExpression(),
        }}
      />
      {/* Founder 2026-09-21: single trees are yellow dots from the split zoom
          up to FOREST_PIN_MIN_ZOOM, and tree pins (icon, green) from there.
          Same source rows as the pins, so a tap opens the same photo. */}
      {kind === 'forests' && (
        <Layer
          id="forests-dots"
          type="circle"
          filter={['!', ['has', 'point_count']]}
          maxzoom={FOREST_PIN_MIN_ZOOM}
          paint={{
            'circle-color': DOT_COLOUR,
            'circle-radius': CLUSTER_MIN_RADIUS,
            'circle-stroke-color': DOT_OUTLINE_COLOUR,
            'circle-stroke-width': DOT_OUTLINE_WIDTH,
          }}
        />
      )}
      {/* Founder 2026-09-21: no static halo ring on any single pin (the icon
          images lost theirs too). The ring layer stays for BOTH kinds at
          opacity 0 and only the hovered / selected pin's ring ripples in. */}
      {PIN_RING_KINDS.includes(kind) && (
        <Layer
          id={`${kind}-pin-ring`}
          type="circle"
          filter={['!', ['has', 'point_count']]}
          paint={{
            'circle-opacity': 0,
            'circle-radius': PIN_RING.radius,
            'circle-stroke-color': '#ffffff',
            'circle-stroke-width': PIN_RING.width,
            'circle-stroke-opacity': PIN_RING.staticOpacity,
            'circle-translate': [0, PIN_RING.translateY],
          }}
        />
      )}
      <Layer
        id={`${kind}-marker`}
        type="symbol"
        filter={['!', ['has', 'point_count']]}
        // Tree pins take over from the yellow dots at FOREST_PIN_MIN_ZOOM;
        // farmer pins show at every zoom past the split, as before.
        minzoom={kind === 'forests' ? FOREST_PIN_MIN_ZOOM : 0}
        layout={{
          'icon-image': `${kind}-marker`,
          'icon-size': 1.2,
        }}
        paint={{
          'icon-opacity': 1,
        }}
      />
    </Source>
  );
};

export const FarmersLayer = ({ propsData }: { propsData: any }) => (
  <ClusteredPinLayer kind="farmers" propsData={propsData} />
);

export const ForestsLayer = ({ propsData }: { propsData: any }) => (
  <ClusteredPinLayer kind="forests" propsData={propsData} />
);
